import { getRouteApi } from '@tanstack/react-router';
import {
  BuildingIcon,
  CloudDownloadIcon,
  FileTextIcon,
  FolderOpenIcon,
  HouseIcon,
  MapPinIcon,
  ScanLine,
} from 'lucide-react';
import type React from 'react';
import { useTranslation } from 'react-i18next';

import { env } from '@/constants/env';
import { cn } from '@/lib/utils';
import { parseStringToBool } from '@/utils/parseStringToBool';

import { canRead } from '@/features/permissions/resource-permissions';
import { NavbarLink } from '../ui/navbar-link';

export type DashboardNavProps = React.ComponentProps<'nav'> & { type: 'estate' | 'project' };

export const DashboardNav = ({ className, type, ...props }: DashboardNavProps) => {
  const { t } = useTranslation();
  const route = getRouteApi(
    type === 'estate'
      ? '/_authenticated/_sidebar-layout/estates/$estateId/_dashboard-layout'
      : '/_authenticated/_sidebar-layout/projects/$projectId/_dashboard-layout'
  );
  const params = route.useParams();

  const data = route.useLoaderData();

  const permissions = data.metadata?.permissions;
  const canReadGeneral = canRead(permissions, 'general');
  const canReadMedia = canRead(permissions, 'media');
  const showPricehubble = parseStringToBool(env.SHOW_PRICEHUBBLE);
  const canReadDocuments =
    canRead(permissions, 'documentsInternal') || canRead(permissions, 'documentsExternal');

  return (
    <nav className={cn('grid auto-cols-fr grid-flow-col gap-1', className)} {...props}>
      {params.projectId ? (
        <>
          <NavbarLink
            title={t('projectDashboard')}
            icon={<BuildingIcon />}
            to="/projects/$projectId"
            activeOptions={{
              exact: true,
            }}
            hide={!canReadGeneral}
          >
            {t('projectDashboard')}
          </NavbarLink>
          <NavbarLink
            title={t('objectDashboard')}
            icon={<HouseIcon />}
            to="/projects/$projectId/objects"
            hide={!canReadGeneral}
          >
            {t('objectDashboard')}
          </NavbarLink>
          <NavbarLink
            title={t('mediaDatabase')}
            to="/projects/$projectId/media"
            icon={<FolderOpenIcon />}
            hide={!canReadMedia}
          >
            {t('mediaDatabase')}
          </NavbarLink>
          <NavbarLink
            title={t('flatfinderFormFlatfinder')}
            to="/projects/$projectId/flatfinder"
            icon={<ScanLine />}
            hide={!canReadGeneral || !canReadMedia}
          >
            {t('flatfinderFormFlatfinder')}
          </NavbarLink>
          <NavbarLink
            title={t('documents')}
            to="/projects/$projectId/documents"
            icon={<CloudDownloadIcon />}
            hide={!canReadDocuments}
          >
            {t('documents')}
          </NavbarLink>
        </>
      ) : (
        <>
          <NavbarLink
            title={t('objectDashboard')}
            icon={<MapPinIcon />}
            to="/estates/$estateId"
            activeOptions={{
              exact: true,
            }}
            hide={!canReadGeneral}
          >
            {t('objectDashboard')}
          </NavbarLink>
          <NavbarLink
            title={t('mediaDatabase')}
            to="/estates/$estateId/media"
            icon={<FolderOpenIcon />}
            hide={!canReadMedia}
          >
            {t('mediaDatabase')}
          </NavbarLink>
          <NavbarLink
            title={t('analytics')}
            to="/estates/$estateId/analytics"
            icon={<FileTextIcon />}
            hide={!canReadGeneral || !showPricehubble}
          >
            {t('analytics')}
          </NavbarLink>
          <NavbarLink
            title={t('documents')}
            to="/estates/$estateId/documents"
            icon={<CloudDownloadIcon />}
            hide={!canReadDocuments}
          >
            {t('documents')}
          </NavbarLink>
        </>
      )}
    </nav>
  );
};
